import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import FloatingActionButton from '../components/FloatingActionButton';
import HomeCharts from '../components/home/HomeCharts';
import MobileRecentSessions from '../components/home/MobileRecentSessions';
import DesktopRecentSessionsTable from '../components/home/DesktopRecentSessionsTable';
import ReleaseBanner from '../components/home/ReleaseBanner';
import { useAuth } from '../context/AuthContext';
import { createActivityNameMap, getActivities, getActivityName } from '../services/activityService';
import { createTrainingClassMap, getTrainingClassLabel, getTrainingClasses } from '../services/trainingClassService';
import { getTrainingSessions } from '../services/trainingSessionService';
import { parseSessionDate } from './homePageUtils';
import './HomePage.css';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

const getChartColors = (theme) => theme.customTokens?.chartPalette || [theme.palette.primary.main];

const getWeekStart = (date) => {
  const localDate = new Date(date);
  const day = localDate.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  localDate.setHours(0, 0, 0, 0);
  localDate.setDate(localDate.getDate() + diffToMonday);
  return localDate;
};

const toWeekKey = (weekStartDate) => {
  const year = weekStartDate.getFullYear();
  const month = String(weekStartDate.getMonth() + 1).padStart(2, '0');
  const day = String(weekStartDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromWeekKey = (weekKey) => {
  const [year, month, day] = weekKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatWeekLabel = (weekStartDate) => {
  const weekEndDate = new Date(weekStartDate.getTime() + (6 * 24 * 60 * 60 * 1000));
  const startLabel = weekStartDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const endLabel = weekEndDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${startLabel} - ${endLabel}`;
};

export default function HomePage() {
  const { accessToken, logout } = useAuth();
  const classPieRef = useRef(null);
  const [sessions, setSessions] = useState([]);
  const [activityNamesById, setActivityNamesById] = useState({});
  const [trainingClassesByName, setTrainingClassesByName] = useState({});
  const [selectedSessionClass, setSelectedSessionClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartColors = useMemo(() => getChartColors(theme), [theme]);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      logout({ shouldCallApi: false });
      return;
    }

    const loadData = async () => {
      try {
        const [sessionsData, activitiesData, trainingClassesData] = await Promise.all([
          getTrainingSessions(accessToken),
          getActivities(accessToken),
          getTrainingClasses(accessToken),
        ]);
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setActivityNamesById(createActivityNameMap(Array.isArray(activitiesData) ? activitiesData : []));
        setTrainingClassesByName(createTrainingClassMap(Array.isArray(trainingClassesData) ? trainingClassesData : []));
      } catch (err) {
        if (err?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }
        setError(err.message || 'Failed to load homepage data.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [accessToken, logout]);

  const recentSessions = useMemo(() => sessions.slice(0, 5), [sessions]);
  const hasSessions = useMemo(() => recentSessions.length > 0, [recentSessions]);

  const twoWeekSessions = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 14);

    return sessions.filter((session) => {
      const parsedDate = parseSessionDate(session.sessionDate);
      return !!parsedDate && parsedDate >= cutoffDate;
    });
  }, [sessions]);

  const sessionClassChartState = useMemo(() => {
    const totalsByClass = twoWeekSessions.reduce((acc, session) => {
      const sessionClass = session.sessionClass || 'other';
      const duration = Number(session.durationMinutes) || 0;
      if (duration <= 0) return acc;
      acc[sessionClass] = (acc[sessionClass] || 0) + duration;
      return acc;
    }, {});

    const sortedEntries = Object.entries(totalsByClass).sort((a, b) => b[1] - a[1]);
    const labels = sortedEntries.map(([sessionClass]) => getTrainingClassLabel(trainingClassesByName, sessionClass));
    const rawLabels = sortedEntries.map(([sessionClass]) => sessionClass);
    const values = sortedEntries.map(([, duration]) => duration);

    return { labels, rawLabels, values };
  }, [trainingClassesByName, twoWeekSessions]);

  const selectedClassActivityChartState = useMemo(() => {
    if (!selectedSessionClass) {
      return { labels: [], values: [] };
    }

    const totalsByActivity = twoWeekSessions
      .filter((session) => session.sessionClass === selectedSessionClass)
      .reduce((acc, session) => {
        (session?.SessionActivity || []).forEach((activity) => {
          const duration = Number(activity.durationMinutes) || 0;
          if (duration <= 0) return;
          const activityName = getActivityName(activityNamesById, activity.activityId);
          acc[activityName] = (acc[activityName] || 0) + duration;
        });
        return acc;
      }, {});

    const sortedEntries = Object.entries(totalsByActivity).sort((a, b) => b[1] - a[1]);
    return {
      labels: sortedEntries.map(([activityName]) => activityName),
      values: sortedEntries.map(([, duration]) => duration),
    };
  }, [activityNamesById, selectedSessionClass, twoWeekSessions]);

  const classPieData = useMemo(() => ({
    labels: sessionClassChartState.labels,
    datasets: [
      {
        label: 'Duration (min)',
        data: sessionClassChartState.values,
        backgroundColor: sessionClassChartState.values.map(
          (_, index) => chartColors[index % chartColors.length]
        ),
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  }), [chartColors, sessionClassChartState.labels, sessionClassChartState.values, theme.palette.background.paper]);

  const activityPieData = useMemo(() => ({
    labels: selectedClassActivityChartState.labels,
    datasets: [
      {
        label: 'Duration (min)',
        data: selectedClassActivityChartState.values,
        backgroundColor: selectedClassActivityChartState.values.map(
          (_, index) => chartColors[index % chartColors.length]
        ),
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  }), [
    chartColors,
    selectedClassActivityChartState.labels,
    selectedClassActivityChartState.values,
    theme.palette.background.paper,
  ]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const value = Number(context.raw) || 0;
            return `${context.label}: ${value} min`;
          },
        },
      },
    },
  }), [theme]);

  const weeklyTrainingChartState = useMemo(() => {
    const totalsByWeek = sessions.reduce((acc, session) => {
      const parsedDate = parseSessionDate(session.sessionDate);
      const duration = Number(session.durationMinutes) || 0;
      if (!parsedDate || duration <= 0) {
        return acc;
      }

      const weekStart = getWeekStart(parsedDate);
      const weekKey = toWeekKey(weekStart);
      acc[weekKey] = (acc[weekKey] || 0) + duration;
      return acc;
    }, {});

    const sortedEntries = Object.entries(totalsByWeek)
      .sort((a, b) => fromWeekKey(a[0]).getTime() - fromWeekKey(b[0]).getTime());

    if (sortedEntries.length === 0) {
      return { labels: [], values: [] };
    }

    const firstWeek = fromWeekKey(sortedEntries[0][0]);
    const lastWeek = fromWeekKey(sortedEntries[sortedEntries.length - 1][0]);
    const entryMap = new Map(sortedEntries);
    const labels = [];
    const values = [];

    for (
      let currentWeek = new Date(firstWeek);
      currentWeek <= lastWeek;
      currentWeek = new Date(currentWeek)
    ) {
      const weekKey = toWeekKey(currentWeek);
      labels.push(formatWeekLabel(currentWeek));
      values.push(Number(entryMap.get(weekKey)) || 0);
      currentWeek.setDate(currentWeek.getDate() + 7);
    }

    return { labels, values };
  }, [sessions]);

  const weeklyLineData = useMemo(() => ({
    labels: weeklyTrainingChartState.labels,
    datasets: [
      {
        label: 'Total Duration (min)',
        data: weeklyTrainingChartState.values,
        borderColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        backgroundColor: theme.customTokens?.lineFill || theme.palette.primary.main,
        tension: 0.25,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
        fill: true,
      },
    ],
  }), [theme, weeklyTrainingChartState.labels, weeklyTrainingChartState.values]);

  const lineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${Number(context.raw) || 0} min`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          callback: (value) => `${value}m`,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
    },
  }), [theme]);

  const toggleRow = (sessionId) => {
    setOpenRows((prev) => ({ ...prev, [sessionId]: !prev[sessionId] }));
  };

  return (
    <Box className="home-page">
      <ReleaseBanner />

      {!loading && (
        <>
          <Box className="home-charts-header">
            <Typography variant="h6" component="h2" className="home-charts-title">
              Training Insights
            </Typography>
            <Link
              component={RouterLink}
              to="/dashboard"
              underline="hover"
              className="home-section-link"
            >
              View dashboard
            </Link>
          </Box>

          <HomeCharts
            classPieRef={classPieRef}
            selectedSessionClass={selectedSessionClass}
            setSelectedSessionClass={setSelectedSessionClass}
            selectedClassActivityChartState={selectedClassActivityChartState}
            activityPieData={activityPieData}
            pieOptions={pieOptions}
            sessionClassChartState={sessionClassChartState}
            classPieData={classPieData}
            weeklyTrainingChartState={weeklyTrainingChartState}
            weeklyLineData={weeklyLineData}
            lineOptions={lineOptions}
            trainingClassesByName={trainingClassesByName}
          />
        </>
      )}

      <Box className="recent-sessions-header">
        <Typography variant="h6" component="h2" className="recent-sessions-title">
          Recent Training Sessions
        </Typography>
        <Link
          component={RouterLink}
          to="/training-history"
          underline="hover"
          className="home-section-link"
        >
          View all
        </Link>
      </Box>

      {loading ? (
        <Box className="home-loading">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!hasSessions ? (
            <Alert severity="info">No training sessions found yet.</Alert>
          ) : isMobile ? (
            <MobileRecentSessions
              recentSessions={recentSessions}
              activityNamesById={activityNamesById}
              trainingClassesByName={trainingClassesByName}
            />
          ) : (
            <DesktopRecentSessionsTable
              recentSessions={recentSessions}
              openRows={openRows}
              onToggleRow={toggleRow}
              activityNamesById={activityNamesById}
              trainingClassesByName={trainingClassesByName}
            />
          )}
        </>
      )}

      <FloatingActionButton onClick={() => navigate('/log-training')}>+</FloatingActionButton>
    </Box>
  );
}
